import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import {styled} from '@mui/material/styles';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import {getColor, getFormattedValue} from 'main/utility/helper/FormatUtil';
import WidgetAction from 'pages/uiwisePages/UiWiseWidget/WidgetAction';
import PropTypes from 'prop-types';
import {UiWiseProgressBarFormat} from 'shared/constants/UiWiseConst';

const StyledTableCell = styled(TableCell)(() => ({
  fontSize: 13.5,
  padding: 8,
}));

const StyledTableRow = styled(TableRow)(({theme}) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  transition: 'background-color 0.2s ease',

  '& td': {
    verticalAlign: 'middle',

    '&:first-of-type': {
      paddingLeft: 20, // Matches the Header Checkbox anchor
    },
    '&:last-of-type': {
      paddingRight: 20,
    },
  },
}));

const TableRows = ({
  dataRow,
  tableSpec,
  checked,
  onChangeCheckedRows,
  dataColumns = [],
}) => {
  const rowId = dataRow[tableSpec.idfieldname];
  return (
    <StyledTableRow key={rowId} className='item-hover'>
      {tableSpec.checkboxselection && (
        <TableCell
          padding='checkbox'
          onClick={(e) => e.stopPropagation()}
          sx={{verticalAlign: 'middle'}}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Checkbox
              sx={{
                color: 'text.disabled',
                p: 0,
                lineHeight: 0, // Fixes the 1px float
                '& .MuiSvgIcon-root': {fontSize: '1.2rem'},
              }}
              color='primary'
              checked={checked}
              onChange={(event) => onChangeCheckedRows(event, rowId)}
            />
          </Box>
        </TableCell>
      )}
      {dataColumns.map((column) => {
        if (!column.show) return null;
        const field = tableSpec.fields.find((f) => f.name === column.name);
        if (!field) return null;
        let content = dataRow[field.name] || field.value;
        const textColor = getColor(content, field.txtcolor);
        const bgColor = getColor(content, field.bgcolor);

        // Transform raw data into formatted content (Date, Currency, etc.)
        content = getFormattedValue(field, content, bgColor, textColor);
        // Case A: Cell is an Action (Link/Button)
        if (field.actions?.length > 0) {
          return (
            <StyledTableCell key={field.name}>
              <Box
                sx={{
                  color: 'primary.dark',
                  fontWeight: 500,
                  bgcolor: bgColor,
                  display: 'inline-block',
                }}
              >
                <WidgetAction
                  action={field.actions[0]}
                  data={dataRow}
                  customLabel={content}
                />
              </Box>
            </StyledTableCell>
          );
        }

        // Case B: Standard Data Cell
        const usePillStyle =
          bgColor && !UiWiseProgressBarFormat.includes(field.format);

        return (
          <StyledTableCell
            key={field.name}
            align={field.halignvalue || 'left'}
            sx={{color: textColor}}
          >
            {usePillStyle ? (
              <Box
                sx={{
                  backgroundColor: bgColor,
                  px: 2,
                  py: 0.5,
                  borderRadius: '4px',
                  textTransform: 'uppercase',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1,
                }}
              >
                {content}
              </Box>
            ) : (
              content
            )}
          </StyledTableCell>
        );
      })}
    </StyledTableRow>
  );
};

TableRows.propTypes = {
  dataRow: PropTypes.object.isRequired,

  tableSpec: PropTypes.shape({
    idfieldname: PropTypes.string.isRequired,
    checkboxselection: PropTypes.bool,
    fields: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        txtcolor: PropTypes.string,
        bgcolor: PropTypes.string,
        halignvalue: PropTypes.string,
        format: PropTypes.string,
        actions: PropTypes.array,
      }),
    ),
  }).isRequired,

  checked: PropTypes.bool,
  onChangeCheckedRows: PropTypes.func,
  dataColumns: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      show: PropTypes.bool,
    }),
  ),
};

export default TableRows;
